import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Trash, ShoppingCart, ArrowLeft, ShieldCheck } from "@phosphor-icons/react";

export default function Cart() {
  const { items, removeItem, total } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const checkout = async () => {
    if (!user) {
      toast.info("Please sign in to complete your purchase.");
      navigate("/login");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/payments/checkout", {
        lookup_keys: items.map((i) => i.lookup_key),
        origin_url: window.location.origin,
      });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error("Could not start checkout. Please try again.");
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="cart-empty">
        <div className="grid place-items-center w-20 h-20 mx-auto rounded-3xl bg-brand-sky/10 mb-6">
          <ShoppingCart size={40} weight="duotone" className="text-brand-sky" />
        </div>
        <h1 className="font-heading font-bold text-3xl text-ink">Your cart is empty</h1>
        <p className="font-body text-ink-soft mt-3">Find the perfect reading package for your little one.</p>
        <Link to="/catalog"><Button className="mt-6 rounded-full bg-brand-sky hover:bg-brand-skylight text-white font-semibold px-8 py-6 text-lg">Browse packages</Button></Link>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12" data-testid="cart-page">
      <Link to="/catalog" className="inline-flex items-center gap-2 font-body font-semibold text-ink-soft hover:text-brand-sky mb-6">
        <ArrowLeft size={18} weight="bold" /> Keep shopping
      </Link>
      <h1 className="font-heading font-bold text-4xl text-ink tracking-tight mb-8">Your cart</h1>

      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-4">
          {items.map((p, i) => (
            <motion.div key={p.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              className="flex items-center gap-4 bg-white sticker rounded-3xl p-4" data-testid={`cart-item-${p.slug}`}>
              <img src={p.image} alt={p.title} className="w-20 h-20 rounded-2xl object-cover" />
              <div className="flex-1">
                <p className="font-body text-xs uppercase tracking-wider" style={{ color: p.color }}>{p.level}</p>
                <p className="font-heading font-bold text-lg text-ink">{p.title}</p>
                <p className="font-body text-sm text-ink-soft">{p.lesson_count} lessons</p>
              </div>
              <span className="font-heading font-bold text-xl text-ink">${Number(p.price).toFixed(0)}</span>
              <Button variant="ghost" size="icon" className="rounded-full hover:bg-brand-coral/10 text-brand-coral" onClick={() => removeItem(p.id)} data-testid={`remove-${p.slug}`}>
                <Trash size={20} weight="duotone" />
              </Button>
            </motion.div>
          ))}
        </div>

        <div className="bg-white sticker rounded-3xl p-6 h-fit sticky top-24">
          <h3 className="font-heading font-bold text-xl text-ink mb-4">Order summary</h3>
          <div className="flex justify-between font-body text-ink-soft mb-2">
            <span>{items.length} package{items.length > 1 ? "s" : ""}</span>
            <span>${total.toFixed(2)}</span>
          </div>
          <div className="border-t border-slate-900/10 my-4" />
          <div className="flex justify-between font-heading font-bold text-2xl text-ink mb-6">
            <span>Total</span><span data-testid="cart-total">${total.toFixed(2)}</span>
          </div>
          <Button onClick={checkout} disabled={loading} className="w-full rounded-full bg-brand-sky hover:bg-brand-skylight text-white font-bold text-lg py-6 transition-transform hover:-translate-y-0.5" data-testid="checkout-btn">
            {loading ? "Redirecting…" : "Checkout securely"}
          </Button>
          <p className="flex items-center justify-center gap-2 mt-4 font-body text-xs text-ink-soft">
            <ShieldCheck size={16} weight="duotone" className="text-brand-grass" /> Secure payment by Stripe
          </p>
        </div>
      </div>
    </div>
  );
}
